import type StoreService from '@ember-data/store';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import Entry from 'garaje/models/entry';
import Invite from 'garaje/models/invite';
import type Location from 'garaje/models/location';
import type VisitorProfileModel from 'garaje/models/visitor-profile';
import type UploaderFactoryService from 'garaje/services/uploader-factory';

import urlBuilder from './url-builder';

interface CreateOptions {
  location: Location;
  model: Entry | Invite;
  store: StoreService;
  /**
   * When true, "block at THIS location" is scoped via the company + a single `locations`
   * (blacklist-locations) entry, because the legacy single `location` rel now produces a company-wide
   * filter. Off (default) keeps the legacy `location` rel.
   */
  useLocationScoping?: boolean;
}

interface CreateFromProfileOptions {
  location: Location;
  profile: VisitorProfileModel;
  store: StoreService;
  /** Same semantics as CreateOptions.useLocationScoping. */
  useLocationScoping?: boolean;
  /**
   * Already-masked document numbers for the modal's "Document ID #" row, read from
   * `GET /visitor-profiles/:id/identity-documents`. A parameter rather than something derived from
   * `profile`, because the profile resource cannot supply them: envoy-web
   * `VisitorProfile#formatted_identifiers` hardcodes `identification-document-numbers` empty
   * pending directories support. Omitted by callers that may not read identity documents.
   */
  documentNumbers?: string[];
}

interface SaveOptions {
  blocklistFilter: BlacklistFilter;
  failOnPhotoFetchError: boolean;
  photo: File | null;
  uploaderFactory: UploaderFactoryService;
}

/**
 * Creates a new blocklist filter record from an entry or invite.
 */
export function createBlocklistFilter(options: CreateOptions): BlacklistFilter {
  const { location, model, store, useLocationScoping } = options;

  // Extract phone numbers from userData
  const phoneNumbers = extractPhoneNumber(model);
  // Extract email
  const emails = [];
  if (options.model.email) {
    emails.push(options.model.email);
  }

  // Extract photo (prefer thumbnails.large, fallback to passportAvatar)
  let photoDataUrl = null;

  if (model instanceof Invite) {
    if (model.photoUrl) {
      photoDataUrl = model.photoUrl;
    }
  }

  if (model instanceof Entry) {
    if (model.thumbnails?.original) {
      photoDataUrl = model.thumbnails.original;
    } else if (model.thumbnails?.large) {
      photoDataUrl = model.thumbnails.large;
    } else if (model.passportAvatar) {
      photoDataUrl = model.passportAvatar;
    }
  }

  const sharedAttrs = {
    fullName: model.fullName || '',
    emails,
    phoneNumbers,
    photo: {
      original: null,
      thumb: null,
      dataUrl: photoDataUrl,
    },
  };

  if (useLocationScoping) {
    // Company-scoped with a single blacklist-locations entry keyed by the location id (peek-or-push).
    const company = location.belongsTo('company').value();
    // Fail loud: a null company (relationship not sideloaded) would POST a mis-scoped, company-less
    // filter while `scope` still reads specific_locations. Callers load it via the route.
    if (!company) {
      throw new Error(
        '[blocklist] createBlocklistFilter: location.company is not loaded — sideload the company ' +
          'relationship before creating a location-scoped filter.',
      );
    }
    const blacklistLocation =
      store.peekRecord('blacklist-location', location.id) ??
      store.push({ data: { id: location.id, type: 'blacklist-location' } });
    return store.createRecord('blacklist-filter', {
      ...sharedAttrs,
      company,
      locations: [blacklistLocation],
    });
  }

  // Legacy (flag off): the single `location` rel.
  return store.createRecord('blacklist-filter', {
    ...sharedAttrs,
    location,
  });
}

/**
 * Case-insensitive dedupe that keeps the first spelling seen and drops blanks. A profile's alias
 * list routinely repeats its own display name, so the seeder and the read-only matched-field table
 * must agree on what counts as a duplicate — otherwise the table shows a row the filter never
 * really gained.
 */
export function dedupeIdentifiers(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();

  return values.reduce<string[]>((kept, raw) => {
    const value = raw?.trim();
    if (!value) return kept;

    const key = value.toLowerCase();
    if (seen.has(key)) return kept;

    seen.add(key);
    kept.push(value);
    return kept;
  }, []);
}

/**
 * Masks all but the last four characters (`D8B800689` → `•••••0689`).
 *
 * Applied to values that already arrive masked: the identity-documents endpoint returns
 * `*****567`, and the directories service — not envoy-web — decides that. The guard below passes
 * those through untouched instead of emitting `•••*567`, while a raw number from any other source
 * is still masked here, so neither shape can leak.
 */
export function maskDocumentNumber(value: string): string {
  const trimmed = value.trim();
  const VISIBLE_SUFFIX = 4;

  // The backend already masks with asterisks (`****789`), so re-masking would emit `•••*789`.
  if (/^[•*]+[^•*]{0,4}$/.test(trimmed)) return trimmed;

  if (trimmed.length <= VISIBLE_SUFFIX) return '•'.repeat(trimmed.length);

  return '•'.repeat(trimmed.length - VISIBLE_SUFFIX) + trimmed.slice(-VISIBLE_SUFFIX);
}

/**
 * Creates a new blocklist filter record seeded from a visitor profile
 *. Kept separate from `createBlocklistFilter` (which is typed to
 * `Entry | Invite` and reads their field shapes) because a VisitorProfile
 * exposes different fields (displayName/legalDisplayName/primaryEmail/
 * primaryPhoneNumber/photoUrl) and additionally carries the `visitorProfile`
 * relationship so the POST links the filter to the profile. The scope branch
 * intentionally mirrors `createBlocklistFilter` to avoid changing that
 * battle-tested Entry/Invite path.
 */
export function createBlocklistFilterFromProfile(options: CreateFromProfileOptions): BlacklistFilter {
  const { documentNumbers = [], location, profile, store, useLocationScoping } = options;

  // Seed the full matched-field set from the profile: name + aliases + every
  // email/phone (primary unioned with the profile's identifier lists), so a
  // profile-linked entry mirrors the profile's identity, not just its primaries.
  const identifiers = profile.identifiers;
  const fullName = profile.legalDisplayName || profile.displayName || '';
  // Drop any alias that just repeats the name we are already sending as fullName — otherwise every
  // profile-seeded filter persists a redundant alias, permanently, on the POST.
  const aliases = dedupeIdentifiers([fullName, ...(identifiers?.aliases ?? [])]).filter(
    (alias) => alias !== fullName.trim(),
  );
  const emails = dedupeIdentifiers([profile.primaryEmail, ...(identifiers?.emails ?? [])]);
  const phoneNumbers = dedupeIdentifiers([profile.primaryPhoneNumber, ...(identifiers?.['phone-numbers'] ?? [])]);

  const sharedAttrs = {
    fullName,
    aliases,
    emails,
    phoneNumbers,
    photo: {
      original: null,
      thumb: null,
      dataUrl: profile.photoUrl,
    },
  };

  // Link the filter to the profile (slimmed `blacklist-visitor-profile` type) so the POST carries
  // the relationship. Pushed WITH the identifier set rather than as a bare linkage: the modal's
  // "Document ID #" row reads `identification-document-numbers` off THIS record, so an
  // attribute-less push leaves `identifiers` at the model's null default and the row renders
  // absent, not blank. Keys mirror envoy-web `VisitorProfile#formatted_identifiers`.
  const seeded = store.peekRecord('blacklist-visitor-profile', profile.id);
  const visitorProfile = store.push({
    data: {
      id: profile.id,
      type: 'blacklist-visitor-profile',
      attributes: {
        identifiers: {
          emails: identifiers?.emails ?? [],
          aliases: identifiers?.aliases ?? [],
          'phone-numbers': identifiers?.['phone-numbers'] ?? [],
          // `push` upserts, so keep numbers an API payload already supplied: a failed lookup hands
          // us an empty list, which would otherwise blank a row elsewhere on the page.
          'identification-document-numbers': documentNumbers.length
            ? documentNumbers
            : (seeded?.identifiers?.['identification-document-numbers'] ?? []),
        },
      },
    },
  });

  if (useLocationScoping) {
    const company = location.belongsTo('company').value();
    if (!company) {
      throw new Error(
        '[blocklist] createBlocklistFilterFromProfile: location.company is not loaded — sideload the ' +
          'company relationship before creating a location-scoped filter.',
      );
    }
    const blacklistLocation =
      store.peekRecord('blacklist-location', location.id) ??
      store.push({ data: { id: location.id, type: 'blacklist-location' } });
    return store.createRecord('blacklist-filter', {
      ...sharedAttrs,
      company,
      locations: [blacklistLocation],
      visitorProfile,
    });
  }

  return store.createRecord('blacklist-filter', {
    ...sharedAttrs,
    location,
    visitorProfile,
  });
}

/**
 * Saves a blocklist filter and its photo if it has one.
 */
/**
 * A seeded photo (from an entry, invite, or visitor profile) arrives as a remote
 * URL on `photo.dataUrl`. `photo` is `serialize: false`, so saving the record
 * alone drops it — it has to be fetched into a File and uploaded separately.
 * Returns null when there is nothing to seed.
 */
export async function seededPhotoAsFile(
  blocklistFilter: BlacklistFilter,
  { failOnFetchError = false }: { failOnFetchError?: boolean } = {},
): Promise<File | null> {
  if (!blocklistFilter.isNew || !blocklistFilter.photo?.dataUrl) return null;

  try {
    // cache: 'no-store' avoids a CORS failure when the browser has cached
    // a prior no-cors <img> load of the same URL (which S3 returns without
    // an Access-Control-Allow-Origin header). See SQ-7049.
    const blob = await (await fetch(<string>blocklistFilter.photo.dataUrl, { cache: 'no-store' })).blob();
    return new File([blob], 'photo.png', { type: 'image/png' });
  } catch (_e) {
    if (failOnFetchError) {
      throw new Error('Blocklist photo upload failed. Please try again.');
    }
    return null;
  }
}

export async function saveBlocklistFilter(options: SaveOptions): Promise<void> {
  const { blocklistFilter, failOnPhotoFetchError, uploaderFactory } = options;
  let { photo } = options;

  if (!photo) {
    photo = await seededPhotoAsFile(blocklistFilter, { failOnFetchError: failOnPhotoFetchError });
  }

  await blocklistFilter.save();

  if (photo) {
    const uploader = uploaderFactory.createUploader({
      url: urlBuilder.v3.blocklistFilter.photoUpload(blocklistFilter.id),
      method: 'POST',
      headers: { Accept: 'application/vnd.api+json' },
    });
    await uploader.upload(photo);
  }
}

/**
 * Extracts phone numbers from an entry or invite's userData.
 */
export function extractPhoneNumber(model: Entry | Invite): string[] {
  const phoneNumbers: string[] = [];

  // Search userData for phone number fields
  const userData = model.userData || [];
  for (const userDatum of userData) {
    const fieldLabel = userDatum.field?.toLowerCase() ?? '';
    if (fieldLabel.includes('phone') || fieldLabel === 'phonenumber' || fieldLabel === 'your phone number') {
      const phoneValue = userDatum.value;
      if (phoneValue && typeof phoneValue === 'string') {
        phoneNumbers.push(phoneValue);
      }
    }
  }

  return phoneNumbers;
}
